import { ChangeDetectionStrategy, Component, effect, inject, input, OnDestroy, output, signal, ViewChild, ViewContainerRef } from '@angular/core';
import { Task } from '../../shared/entities/task/task';
import { CommandInput, CommandTemplate } from '../../../core/entities/commandTemplate/commandTemplate';
import { CommandService } from '../../../core/services/command/command.service';
import { LoadingService } from '../../../core/services/loading.service';
import { RefetchCoordinator } from '../../../core/services/refetch-coordinator/refetch-coordinator.service';
import { take } from 'rxjs';
import { ControlTemplate } from '../form/entities/controlTemplate';
import { TaskFormHandlerRegistry } from './handlers/task-form-handler-registry.service';
import { TaskFormHandler } from './handlers/task-form-handler.interface';

@Component({
  selector: 'app-task-complete',
  templateUrl: './task-complete.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskCompleteComponent implements OnDestroy {

  readonly task = input<Task>();
  
  // Injected services
  readonly commandService = inject(CommandService);
  private readonly handlerRegistry = inject(TaskFormHandlerRegistry);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  private readonly loadingService = inject(LoadingService);
  
  readonly commandTemplate = signal<CommandTemplate | null>(null);
  readonly formControlTemplate = signal<ControlTemplate | null>(null);
  readonly disabled = signal<boolean>(true);
  readonly handlerState = signal<any>(null); // Custom state from current handler

  readonly updateTitle = output<string>();

  @ViewChild('handlerOutlet', { read: ViewContainerRef, static: false }) 
  handlerOutlet?: ViewContainerRef;

  private currentHandler: TaskFormHandler | null = null;
  private currentComponentRef: any = null;

  constructor() {
    // React to task changes
    effect(() => {
      const task = this.task();
      if (task) {
        this.loadCommandTemplate();
      }
    });
  }

  loadCommandTemplate() {
    const task = this.task();
    if (!task) return;
    
    const commandInput = new CommandInput();
    commandInput.name = "taskCompleteCommand";
    commandInput.addParameter('commandProviderId', task.id);
    commandInput.addParameter('deviceTarget', "WEB");
      
    this.commandService.getCommandByCommandInput(commandInput)
        .pipe(take(1))
        .subscribe(result => {
          this.commandTemplate.set(result.data.getCommandByCommandInput);
          this.disabled.set(result.data.getCommandByCommandInput.settings?.disabled ?? true);
          if(result.data.getCommandByCommandInput?.flow?.settings?.customFlowSettings?.form) {
            this.formControlTemplate.set(new ControlTemplate(result.data.getCommandByCommandInput.flow.settings.customFlowSettings.form));
            if(this.formControlTemplate()) {
              // override title with form label
              if(this.formControlTemplate()?.settings?.label) {
                this.updateTitle.emit(this.formControlTemplate()?.settings?.label);
              }
              
              // Initialize form handler based on form key
              this.initializeFormHandler();
            }
          } else {
            // No form - use default handler
            this.initializeFormHandler();
          }
        });
  }

  private initializeFormHandler(): void {
    
    // Clean up previous component
    if (this.currentComponentRef) {
      this.currentComponentRef.destroy();
      this.currentComponentRef = null;
    }

    // Get form key from template
    const formKey = this.formControlTemplate()?.key;
    
    // Get appropriate handler from registry
    this.currentHandler = this.handlerRegistry.getHandler(formKey);
    
    const task = this.task();
    if (this.currentHandler && task) {
      console.log(`[TaskCompleteComponent] Using handler: ${this.currentHandler.formKey} for form key: ${formKey}`);
      
      // Initialize the handler with task and template
      this.currentHandler.initialize(
        task,
        this.formControlTemplate()
      );
      
      // Expose handler state to template
      if (this.currentHandler.getState) {
        this.handlerState.set(this.currentHandler.getState());
      }
      
      // Load handler-specific component if provided
      this.loadHandlerComponent();
    } else {
      console.warn(`[TaskCompleteComponent] No handler found for form key: ${formKey}`);
    }
  }
  
  private loadHandlerComponent(): void {
    if (!this.handlerOutlet) {
      return;
    }
    
    this.handlerOutlet.clear();
    
    if (this.currentHandler?.getComponent) {
      const component = this.currentHandler.getComponent();
      
      if (component) {
        this.currentComponentRef = this.handlerOutlet.createComponent(component);
        
        // Pass data to the component instance using setInput for signal inputs
        const task = this.task();
        const handler = this.currentHandler;
        
        if (task && handler) {
          console.log(this.formControlTemplate());
          this.currentComponentRef.setInput('task', task);
          this.currentComponentRef.setInput('template', this.formControlTemplate());
          this.currentComponentRef.setInput('commandTemplate', this.commandTemplate());
          this.currentComponentRef.setInput('disabled', this.disabled());
          this.currentComponentRef.setInput('handler', handler);
          
          // Subscribe to complete event if component has it
          const complete = this.currentComponentRef.instance.complete;
          if (complete) {
            complete.subscribe((data: any) => {
              this.handleComplete(data);
            });
          }
        }
      }
    }
  }
  
  handleComplete(formModelInput?: any): void {
    const task = this.task();
    const commandTemplate = this.commandTemplate();
    
    if (!task?.id || !commandTemplate?.name) {
      console.error('[TaskCompleteComponent] Missing task or command');
      return;
    }

    console.log('[TaskCompleteComponent] Handling completion for task:', task.id);
    console.log('[TaskCompleteComponent] Form data:', formModelInput);

    // Build CommandInput
    const commandInput = new CommandInput();
    commandInput.name = commandTemplate.name;
    commandInput.addParameter('commandProviderId', task.id);
    
    // Add form data if available and not empty
    if (formModelInput && Object.keys(formModelInput).length > 0) {
      commandInput.addParameter('formModelInput', formModelInput);
    }

    // Send command
    this.loadingService.setLoading(true);
    this.commandService.sendCommand(commandInput, (result) => {
      console.log('[TaskCompleteComponent] Command completed:', result);
      // Trigger refetch or navigation
      this.refetchCoordinator.emit({ 
        entityType: task.getEntityType(), 
        entityId: task.id 
      });
    });
  }

  ngOnDestroy(): void {
    // Clean up current handler
    if (this.currentHandler?.destroy) {
      this.currentHandler.destroy();
    }
    
    // Clean up current component
    if (this.currentComponentRef) {
      this.currentComponentRef.destroy();
    }
  }
  
}
